import { type FichaDeAgencia, IDIOMAS_DE_AGENCIA, MAXIMO_DE_SERVICIOS_DE_AGENCIA, PAISES_DE_AGENCIA, revisarFichaDeAgencia, SERVICIOS_DE_AGENCIA } from '@mc/socios'

import { ErrorDeLaApi } from '../api/nucleo'
import type { AgenciaSocia, CuerpoDeFichaDeAgencia } from '../api/tipos'

/**
 * La ficha de una agencia socia en el formulario del panel (CLAUDE.md §30). Las reglas
 * no están aquí: las pone `revisarFichaDeAgencia`, la misma función con la que el
 * núcleo decide. Aquí solo se pasa de lo que llega del núcleo a lo que se escribe, y
 * de lo que se escribe a lo que se manda.
 */

/** Lo que lleva el formulario mientras se escribe: los textos tal cual y el logo con su dirección, para la vista previa. */
export type Borrador = {
  empresa: string
  web: string
  correo: string
  telefono: string
  descripcion: string
  ciudad: string
  pais: string
  servicios: string[]
  idiomas: string[]
  logo: null | { id: number; url: null | string }
  directorio: boolean
  contactoPublico: boolean
}

export type CampoDeLaFicha = keyof FichaDeAgencia

/** El núcleo se queda con los primeros; el panel no deja marcar más (el número vive en `@mc/socios`). */
export const MAXIMO_DE_SERVICIOS = MAXIMO_DE_SERVICIOS_DE_AGENCIA

type Catalogo = Record<string, { es: string; en: string }>

/** Solo lo que está en el catálogo, sin repetir y en el orden del catálogo: así dos fichas iguales se comparan iguales. */
const enElCatalogo = (valor: unknown, catalogo: Catalogo): string[] => {
  if (!Array.isArray(valor)) return []
  const elegidos = new Set(valor.filter((v): v is string => typeof v === 'string'))
  return Object.keys(catalogo).filter((clave) => elegidos.has(clave))
}

/**
 * El formulario a partir de la ficha guardada, o vacío. Por omisión la agencia quiere
 * salir en el directorio (es para lo que se apunta) y no enseña su contacto; y atiende
 * en español, que es el mercado.
 */
export const borradorDe = (agencia: AgenciaSocia | null | undefined): Borrador => {
  const idiomas = enElCatalogo(agencia?.idiomas, IDIOMAS_DE_AGENCIA)
  const pais = (agencia?.pais ?? '').toUpperCase()
  return {
    empresa: agencia?.empresa ?? '',
    web: agencia?.web ?? '',
    correo: agencia?.correo ?? '',
    telefono: agencia?.telefono ?? '',
    descripcion: agencia?.descripcion ?? '',
    ciudad: agencia?.ciudad ?? '',
    pais: Object.hasOwn(PAISES_DE_AGENCIA, pais) ? pais : '',
    servicios: enElCatalogo(agencia?.servicios, SERVICIOS_DE_AGENCIA),
    idiomas: idiomas.length ? idiomas : ['es'],
    logo: typeof agencia?.logo === 'number' && agencia.logo > 0 ? { id: agencia.logo, url: agencia.logoUrl ?? null } : null,
    directorio: agencia?.directorio ?? true,
    contactoPublico: agencia?.contactoPublico ?? false,
  }
}

/** Lo que se manda al núcleo. El logo va siempre: `null` es «quitarlo». */
export const cuerpoDe = (b: Borrador): CuerpoDeFichaDeAgencia => ({
  empresa: b.empresa,
  web: b.web,
  correo: b.correo,
  telefono: b.telefono,
  descripcion: b.descripcion,
  ciudad: b.ciudad,
  pais: b.pais,
  servicios: b.servicios,
  idiomas: b.idiomas,
  logo: b.logo?.id ?? null,
  directorio: b.directorio,
  contactoPublico: b.contactoPublico,
})

/** El primer problema de la ficha, con su campo, o `null`: la misma respuesta que daría el núcleo. */
export const problemaDe = (b: Borrador): null | { campo: CampoDeLaFicha; mensaje: string } => {
  const r = revisarFichaDeAgencia(cuerpoDe(b))
  return r.ok ? null : { campo: r.campo, mensaje: r.mensaje }
}

/** La ficha como la deja el núcleo (recortada, con `https://`…), sin tocar el logo. Es lo que se ve después de guardar. */
export const limpiar = (b: Borrador): Borrador => {
  const r = revisarFichaDeAgencia(cuerpoDe(b))
  return r.ok ? { ...b, ...r.ficha, servicios: [...r.ficha.servicios], idiomas: [...r.ficha.idiomas] } : b
}

/** Si hay algo que guardar: se compara lo que se mandaría, no cómo está escrito cada espacio. */
export const hayCambios = (antes: Borrador, ahora: Borrador): boolean => JSON.stringify(cuerpoDe(limpiar(antes))) !== JSON.stringify(cuerpoDe(limpiar(ahora)))

/** Marca o desmarca una opción de un catálogo, en su orden, sin pasar del máximo. */
export const alternar = (lista: string[], valor: string, catalogo: Catalogo, maximo = Number.POSITIVE_INFINITY): string[] => {
  if (!Object.hasOwn(catalogo, valor)) return lista
  if (lista.includes(valor)) return lista.filter((v) => v !== valor)
  if (lista.length >= maximo) return lista
  return enElCatalogo([...lista, valor], catalogo)
}

/** El campo que señala un 400 del núcleo (`datos.campo`), si es uno de la ficha. */
export const campoDelFallo = (fallo: unknown): CampoDeLaFicha | null => {
  if (!(fallo instanceof ErrorDeLaApi)) return null
  const campo = fallo.datos.campo
  const campos: readonly string[] = ['empresa', 'web', 'correo', 'telefono', 'descripcion', 'ciudad', 'pais', 'servicios', 'idiomas', 'directorio', 'contactoPublico']
  return typeof campo === 'string' && campos.includes(campo) ? (campo as CampoDeLaFicha) : null
}

// --- La tarjeta del directorio ------------------------------------------------------------

/** Lo que no es nombre: «S.A.C.», «S.R.L.», «Inc.»… no dan iniciales. */
const SOCIEDAD = /^(?:(?:[a-z]\.){1,4}[a-z]?\.?|s\.?a\.?c|s\.?r\.?l|e\.?i\.?r\.?l|sac|srl|eirl|sa|sl|llc|inc\.?|ltd\.?|y|&|de|la|el|los|las)$/i

/** «Monstruo Creativo S.A.C.» → «MC»: lo que se pinta cuando no hay logo. */
export const iniciales = (empresa: string): string => {
  const palabras = empresa
    .trim()
    .split(/\s+/)
    .filter((p) => /^[\p{L}\p{N}]/u.test(p) && !SOCIEDAD.test(p))
  const letras = palabras.slice(0, 2).map((p) => [...p][0]!.toLocaleUpperCase('es'))
  return letras.join('') || [...empresa.trim()][0]?.toLocaleUpperCase('es') || '?'
}

/** Un trozo de la descripción que acaba en una palabra entera, con «…» si se ha cortado. */
export const extracto = (texto: string, maximo = 180): string => {
  const limpio = texto.trim().replace(/\s+/g, ' ')
  if (limpio.length <= maximo) return limpio
  const corte = limpio.slice(0, maximo + 1)
  const espacio = corte.lastIndexOf(' ')
  return `${(espacio > maximo * 0.6 ? corte.slice(0, espacio) : corte.slice(0, maximo)).replace(/[\s.,;:¡!¿?—–-]+$/u, '')}…`
}

/** «Lima, Perú». */
export const ubicacionDe = (ciudad: null | string | undefined, pais: null | string | undefined): string => {
  const nombre = pais && Object.hasOwn(PAISES_DE_AGENCIA, pais) ? PAISES_DE_AGENCIA[pais as keyof typeof PAISES_DE_AGENCIA].es : ''
  return [ciudad?.trim(), nombre].filter(Boolean).join(', ')
}

/** «https://www.tuagencia.pe/» → «tuagencia.pe», para enseñarla. */
export const dominioDe = (web: null | string | undefined): string => {
  if (!web) return ''
  try {
    return new URL(/^https?:\/\//i.test(web) ? web : `https://${web}`).hostname.replace(/^www\./, '')
  } catch {
    return web
  }
}

/** Las etiquetas en español de unas claves de un catálogo, en su orden y sin las que no conoce. */
export const etiquetasDe = (claves: null | string[] | undefined, catalogo: Catalogo): string[] => enElCatalogo(claves, catalogo).map((c) => catalogo[c]!.es)
